/**
 * Security Assessment + SQA Report Card Service
 * Wraps /api/admin/ai/security-assessment/ + /api/admin/sqa/report-card/* endpoints
 * Phase 4.8-B — WQ-200
 */

// === Types ===

export interface SecurityAssessment {
  id: string;
  artifact_id: string;
  assessment_trigger: string;
  provenance_score: number;
  provenance_source: string | null;
  provenance_origin_blocked: boolean;
  serialization_score: number;
  serialization_format: string | null;
  serialization_scan_result: string | null;
  license_score: number;
  license_status: string | null;
  vulnerability_score: number;
  vulnerability_cve_count: number;
  bias_score: number;
  bias_assessment_status: string | null;
  model_card_score: number;
  model_card_status: string | null;
  drift_score: number;
  drift_status: string | null;
  artifact_trust_score: number;
  overall_risk_level: string;
  threshold_met: boolean;
  integrity_level: string | null;
  summary_statement: string | null;
  assessed_at: string;
  assessed_by: string | null;
}

export interface TrustCard {
  artifact_id: string;
  artifact_trust_score: number;
  overall_risk_level: string;
  dimensions: Record<string, number>;
  assessed_at: string;
}

export interface TrustDashboardItem {
  artifact_id: string;
  artifact_trust_score: number;
  overall_risk_level: string;
  threshold_met: boolean;
  assessed_at: string;
}

export interface SQAReportCard {
  component_id: string;
  component_name: string;
  integrity_level: string;
  confidence_score: number;
  dimensions: Record<string, number>;
  ncr_summary: Record<string, number>;
  last_assessed: string;
}

export interface PlatformReportCard {
  overall_confidence: number;
  component_count: number;
  components: SQAReportCard[];
  ncr_totals: Record<string, number>;
}

export interface CompositeTrustCard {
  artifact_id: string;
  security_score: number;
  sqa_score: number;
  composite_score: number;
  risk_level: string;
}

export interface NCRSummary {
  total: number;
  by_severity: Record<string, number>;
  by_component: Record<string, number>;
  by_age: Record<string, number>;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === Security Assessment API ===

export async function assessArtifact(
  authFetch: FetchFn,
  artifactId: string,
): Promise<SecurityAssessment> {
  const res = await authFetch(`/api/admin/ai/security-assessment/assess/${artifactId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to assess artifact: ${res.status}`);
  return res.json();
}

export async function listAssessments(
  authFetch: FetchFn,
  params?: { artifact_id?: string; risk_level?: string; limit?: number },
): Promise<SecurityAssessment[]> {
  const qs = new URLSearchParams();
  if (params?.artifact_id) qs.set('artifact_id', params.artifact_id);
  if (params?.risk_level) qs.set('risk_level', params.risk_level);
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/security-assessment/assessments${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list assessments: ${res.status}`);
  return res.json();
}

export async function getAssessment(
  authFetch: FetchFn,
  assessmentId: string,
): Promise<SecurityAssessment> {
  const res = await authFetch(`/api/admin/ai/security-assessment/assessments/${assessmentId}`);
  if (!res.ok) throw new Error(`Failed to get assessment: ${res.status}`);
  return res.json();
}

export async function getTrustCard(
  authFetch: FetchFn,
  artifactId: string,
): Promise<TrustCard> {
  const res = await authFetch(`/api/admin/ai/security-assessment/trust-card/${artifactId}`);
  if (!res.ok) throw new Error(`Failed to get trust card: ${res.status}`);
  return res.json();
}

export async function getTrustDashboard(authFetch: FetchFn): Promise<TrustDashboardItem[]> {
  const res = await authFetch('/api/admin/ai/security-assessment/dashboard');
  if (!res.ok) throw new Error(`Failed to get trust dashboard: ${res.status}`);
  return res.json();
}

export async function reassessAll(
  authFetch: FetchFn,
): Promise<{ artifacts_assessed: number; assessment_ids: string[] }> {
  const res = await authFetch('/api/admin/ai/security-assessment/reassess-all', {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to reassess all: ${res.status}`);
  return res.json();
}

// === SQA Report Card API ===

export async function getPlatformReportCard(authFetch: FetchFn): Promise<PlatformReportCard> {
  const res = await authFetch('/api/admin/sqa/report-card/platform');
  if (!res.ok) throw new Error(`Failed to get platform report card: ${res.status}`);
  return res.json();
}

export async function getComponentReportCard(
  authFetch: FetchFn,
  componentId: string,
  variant?: 'full' | 'compact' | 'tenant',
): Promise<SQAReportCard> {
  const qs = variant ? `?variant=${variant}` : '';
  const res = await authFetch(`/api/admin/sqa/report-card/${componentId}${qs}`);
  if (!res.ok) throw new Error(`Failed to get component report card: ${res.status}`);
  return res.json();
}

export async function getCompositeTrustCard(
  authFetch: FetchFn,
  artifactId: string,
): Promise<CompositeTrustCard> {
  const res = await authFetch(`/api/admin/sqa/composite-trust-card/${artifactId}`);
  if (!res.ok) throw new Error(`Failed to get composite trust card: ${res.status}`);
  return res.json();
}

export async function getNCRSummary(authFetch: FetchFn): Promise<NCRSummary> {
  const res = await authFetch('/api/admin/sqa/ncr-summary');
  if (!res.ok) throw new Error(`Failed to get NCR summary: ${res.status}`);
  return res.json();
}
